// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EditIcon from '@mui/icons-material/Edit';

// material ui
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

// api
import {
  useSearchStatusesQuery,
  useSearchTicketQuery,
  useSearchTicketTopicsQuery,
} from '@/api/generated/daluxfm/daluxfm';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import EditDaluxTicketDialog from '@/features/dalux-integration/EditDaluxTicketDialog';

// relative
import type { DaluxAccountId, DaluxTicket } from './types';

import dayjs from 'dayjs';

type DaluxTicketStatus = {
  ticketStatusId?: string | number | null;
  name?: string | null;
};

type DaluxTicketRow = {
  id: string;
  number: string;
  topic: string;
  description: string;
  createdAt: string;
  status: string | number;
  statusLabel: string;
  ticket: DaluxTicket;
};

type DaluxTicketsDatagridProps = {
  accountId?: DaluxAccountId;
};

function toTicketRow(
  ticket: DaluxTicket,
  statusesMap: Record<string, string>,
): DaluxTicketRow | null {
  const ticketId = ticket?.data?.ticketId;

  if (!ticketId) {
    return null;
  }

  return {
    id: ticketId,
    number: ticket?.data?.number || '',
    topic: ticket?.data?.topic?.name || ticket?.data?.topic?.topicId || '',
    description: ticket?.data?.description || '',
    createdAt: ticket?.data?.createdDate || '',
    status: ticket?.data?.status ?? '',
    statusLabel:
      statusesMap[String(ticket?.data?.status ?? '')] ||
      String(ticket?.data?.status ?? ''),
    ticket,
  };
}

export default function DaluxTicketsDatagrid({
  accountId,
}: DaluxTicketsDatagridProps) {
  const { t } = useTranslation('dalux-integration');
  const { email } = useSessionAuth();
  const [editingTicket, setEditingTicket] = useState<DaluxTicket | null>(null);

  const { data: ticketsData, isLoading } = useSearchTicketQuery(
    { limit: 100 },
    { skip: !accountId || !email },
  );
  const { data: statusesData } = useSearchStatusesQuery(
    { limit: 100 },
    { skip: !accountId || !email },
  );

  const { data: topics } = useSearchTicketTopicsQuery(
    { limit: 100 },
    { skip: !accountId || !email },
  );

  const statuses = (statusesData?.items || []) as DaluxTicketStatus[];
  const statusesMap = Object.fromEntries(
    statuses
      .filter((status) => status?.ticketStatusId)
      .map((status) => [String(status.ticketStatusId), status?.name || '']),
  );

  const tickets = ((ticketsData?.items || []) as DaluxTicket[])
    .filter(
      (ticket) =>
        ticket?.data?.reporterEmail?.toLowerCase() === email?.toLowerCase(),
    )
    .map((ticket) => toTicketRow(ticket, statusesMap))
    .filter((ticket): ticket is DaluxTicketRow => ticket !== null)
    .sort((left, right) => {
      const leftTime = left.createdAt ? new Date(left.createdAt).getTime() : 0;
      const rightTime = right.createdAt
        ? new Date(right.createdAt).getTime()
        : 0;
      return rightTime - leftTime;
    });

  const columns = [
    {
      field: 'number',
      headerName: t('tickets.columns.number'),
      minWidth: 120,
      flex: 0.6,
    },
    {
      field: 'topic',
      headerName: t('tickets.columns.topic'),
      minWidth: 160,
      flex: 0.8,
      valueFormatter: (value: string) => {
        const topic = topics?.items?.find((t) => t?.topicId === value);
        return topic?.name || '';
      }, // map id to name of the topic.
    },
    {
      field: 'description',
      headerName: t('tickets.columns.description'),
      minWidth: 260,
      flex: 1.6,
    },
    {
      field: 'createdAt',
      headerName: t('tickets.columns.created_at'),
      minWidth: 160,
      flex: 0.8,
      valueFormatter: (value) =>
        value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '',
    },
    {
      field: 'statusLabel',
      headerName: t('tickets.columns.status'),
      minWidth: 100,
      flex: 0.5,
    },
    {
      field: 'actions',
      headerName: t('tickets.columns.actions'),
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Tooltip title={t('tickets.edit')}>
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setEditingTicket(row.ticket)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader
        avatar={<ConfirmationNumberIcon color="primary" />}
        title={t('tickets.title')}
        subheader={t('tickets.description')}
      />
      <CardContent sx={{ pt: 0 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : tickets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('tickets.no_tickets')}
          </Typography>
        ) : (
          <DataGrid
            rows={tickets}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
          />
        )}
      </CardContent>
      <EditDaluxTicketDialog
        open={!!editingTicket}
        onClose={() => setEditingTicket(null)}
        accountId={accountId}
        ticket={editingTicket}
      />
    </Card>
  );
}
